import { apiClient } from '../api/client';
import type {
  InAppNotification,
  ApiResponse,
  PaginatedResponse,
  NotificationStatus,
} from '../types/notification.types';

const API_BASE = '/notifications';

/**
 * In-App Notification Service
 * Manage bell dropdown notifications
 */
export class InAppNotificationService {
  /**
   * Get user's notifications (paginated)
   */
  async getNotifications(filters?: {
    status?: NotificationStatus;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<InAppNotification> & { unreadCount: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const response = await apiClient.get<
      ApiResponse<InAppNotification[]> & { pagination: any; unreadCount: number }
    >(`${API_BASE}?${params.toString()}`);

    return {
      data: response.data.data || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
      },
      unreadCount: response.data.unreadCount || 0,
    };
  }

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<ApiResponse<{ count: number }>>(
      `${API_BASE}/unread-count`
    );
    return response.data.data?.count || 0;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<InAppNotification> {
    const response = await apiClient.put<ApiResponse<InAppNotification>>(
      `${API_BASE}/${id}/read`
    );
    if (!response.data.data) {
      throw new Error('Notification not found');
    }
    return response.data.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ count: number }> {
    const response = await apiClient.post<ApiResponse<{ count: number }>>(
      `${API_BASE}/mark-all-read`
    );
    return { count: response.data.data?.count || 0 };
  }

  /**
   * Archive notification
   */
  async archiveNotification(id: string): Promise<void> {
    await apiClient.put(`${API_BASE}/${id}/archive`);
  }

  /**
   * Delete notification
   */
  async deleteNotification(id: string): Promise<void> {
    await apiClient.delete(`${API_BASE}/${id}`);
  }
}

export const inAppNotificationService = new InAppNotificationService();
